import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, input, OnInit, output, PLATFORM_ID, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '../../../auth/auth.service';
import { Country } from '../../../modules/auth/auth.model';
import { PhoneNumberValidator } from '../../services/validations';
import { map, Observable } from 'rxjs';
import { ToasterService } from '../../services/toaster.service';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-phone-verification-modal',
  imports: [
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './phone-verification-modal.component.html',
  styleUrl: './phone-verification-modal.component.scss'
})
export class PhoneVerificationModalComponent implements OnInit {
  verificationCode = "";
  resendCooldown = signal(true);
  resendCountdown = signal(60);
  private resendInterval: ReturnType<typeof setInterval> | undefined;
  resendSubmitting = signal(false);
  showCodeBox = false;
  countryCode = new FormControl('+61');
  countries: Country[] = [];
  readonly filteredCountries = signal<Country[]>([]);
  readonly showDropdown = signal(false);
  selectedCountry: Country = new Country();
  invalidPhone = false;
  _authService = inject(AuthService);
  _platformId = inject(PLATFORM_ID);
  phoneForm!: FormGroup;
  fb = inject(FormBuilder);
  closeModal = output();
  token = input<string>();
  /** Optional phone number to prefill (e.g. when re-verifying an unverified number) */
  prefillPhone = input<string>('');
  /** Optional dial code (e.g. '+91') to preselect in the country picker */
  prefillDialCode = input<string>('');
  /** Override the default "Add your mobile" title */
  title = input<string>('Add your mobile');
  /** Override the default description copy under the title (e.g. for re-verification) */
  description = input<string>('');
  /**
   * When false, the country + phone inputs are locked (read-only) so the user can
   * only verify the prefilled number. When true (default), the number is editable
   * — used when adding a phone number for the first time.
   */
  editablePhone = input<boolean>(true);
  cd = inject(ChangeDetectorRef);
  toastService = inject(ToasterService);
  enteredNumber = '';
  submittingPhone = false;
  verifyingPhone = signal(false);
  /** Server-side error (e.g. "Phone number is already in use.") shown under the phone field. */
  phoneErrorMessage = signal<string | null>(null);
  /**
   * True when the phone field is editable AND the entered number matches the
   * already-saved one. Used to disable Continue so the user can't submit an
   * unchanged number (which the server rejects as already verified).
   */
  phoneUnchanged = signal(false);
  ngOnInit(): void {
    if (isPlatformBrowser(this._platformId)) {
      this.phoneForm = this.fb.group({
        phone: [this.prefillPhone() || '', Validators.required],
        countryCode: ['', Validators.required]
      });

      (this._authService.getPhoneCodes() as Observable<Country[]>).subscribe({
        next: (res: Country[]) => {
          res.map(country => country.searchQuery = `${country.name} (${country.dial_code})`)
          this.countries = res;
          this.filteredCountries.set(res);
          this.applyPrefillDialCode();
        },
        error: (error: unknown) => {
          console.error('Error fetching phone codes:', error);
        }
      });

      this.phoneForm.controls['countryCode'].valueChanges.pipe(
        map(value => this._filterCountryCode(value || '')),
      ).subscribe((res: Country[]) => {
        this.filteredCountries.set(res);
      });

      this.phoneForm.controls['phone'].valueChanges.subscribe(() => {
        this.updatePhoneUnchanged();
      });

      this.updatePhoneUnchanged();
    }
  }

  /** Recompute whether the editable number is unchanged from the saved one. */
  private updatePhoneUnchanged(): void {
    this.phoneUnchanged.set(this.editablePhone() && this.isPhoneUnchanged());
    this.cd.markForCheck();
  }

  private applyPrefillDialCode(): void {
    const dial = (this.prefillDialCode() || '').trim();
    if (!dial) return;
    const normalised = dial.startsWith('+') ? dial : `+${dial}`;
    const match = this.countries.find(c => c.dial_code === normalised);
    if (!match) return;
    this.selectCountry(match);
    this.cd.markForCheck();
  }

  selectCountry(country: Country) {
    this.phoneForm.controls['countryCode'].setValue(country.searchQuery); // or country.name/code as needed
    this.showDropdown.set(false);
    this.selectedCountry = country;
    const phone = this.phoneForm.controls['phone'].value;
    if (phone) {
      this.validatePhoneNumber(true);
    }
    this.updatePhoneUnchanged();
  }
  private _filterCountryCode(value: string): Country[] {
    const filterValue = value.toLowerCase();
    return this.countries.filter(option => option.searchQuery.toLowerCase().includes(filterValue));
  }

  hideDropdownWithDelay() {
    this.resetPhoneValidation();
    setTimeout(() => this.showDropdown.set(false), 200); // Delay to allow click event
  }

  resetPhoneValidation() {
    const code = this.phoneForm.controls['countryCode'].value;
    const phone = this.phoneForm.controls['phone'].value;
    if (!code) {
      this.selectedCountry = new Country();
      if (!phone) {
        this.invalidPhone = false;
      }
    }
  }

  close() {
    this.closeModal.emit();
    this.showCodeBox = false;
  }
  confirmPhone(isResend = false) {
    if (this.phoneForm.invalid || this.invalidPhone) {
      return;
    }
    this.phoneErrorMessage.set(null);
    this.enteredNumber = this.phoneForm.controls['phone'].value;
    const countryCode = this.phoneForm.controls['countryCode'].value;
    const phoneNumber = {
      phone: this.enteredNumber,
      countryCode
    }

    phoneNumber.countryCode = this.selectedCountry.dial_code;

    // If the number is unchanged from the prefilled (already-saved) one, skip
    // update-phone-number — it would 409 "Phone number is already in use." — and
    // instead call send-verification-sms to (re)send the code for the saved number.
    if (this.isPhoneUnchanged()) {
      this.submittingPhone = true;
      this._authService.sendVerificationSms(this.token()).subscribe({
        next: () => {
          this.startResendCooldown();
          this.submittingPhone = false;
          this.showCodeBox = true;
          if (isResend) {
            this.resendSubmitting.set(false);
            this.toastService.success('Resend code sent successfully', 'Success');
          }
          this.cd.markForCheck();
        },
        error: (error: unknown) => {
          this.submittingPhone = false;
          this.resendSubmitting.set(false);
          this.phoneErrorMessage.set(this.extractErrorMessage(error));
          this.cd.markForCheck();
        }
      });
      return;
    }

    this.submittingPhone = true;
    // No explicit token (logged-in flow) → the interceptor applies cookie-based auth.
    // The sign-up flow still passes a token via the `token` input.
    this._authService.updatePhoneNumber(phoneNumber, this.token()).subscribe({
      next: (res: unknown) => {
        this.startResendCooldown();
        this.submittingPhone = false;
        if (isResend) {
          this.resendSubmitting.set(false);
          this.toastService.success('Resend code sent successfully', 'Success');
        } else {
          this.showCodeBox = true;
        }
        this.cd.markForCheck();
      },
      error: (error: unknown) => {
        this.submittingPhone = false;
        this.resendSubmitting.set(false);
        this.phoneErrorMessage.set(this.extractErrorMessage(error));
        this.cd.markForCheck();
      }
    });

  }

  /** True when the entered number + dial code match the prefilled (already-saved) number. */
  private isPhoneUnchanged(): boolean {
    const prefillPhone = (this.prefillPhone() || '').replace(/\D/g, '');
    if (!prefillPhone) return false;
    const enteredPhone = (this.phoneForm.controls['phone'].value || '').replace(/\D/g, '');
    const prefillDial = (this.prefillDialCode() || '').replace(/\D/g, '');
    const enteredDial = (this.selectedCountry.dial_code || '').replace(/\D/g, '');
    return enteredPhone === prefillPhone && enteredDial === prefillDial;
  }

  /** Pull the server-provided message off an HttpErrorResponse, with a sensible fallback. */
  private extractErrorMessage(error: unknown): string {
    const fallback = 'Error updating phone number. Please try again.';
    const err = error as { error?: { message?: unknown }; message?: unknown };
    const serverMessage = err?.error?.message;
    if (typeof serverMessage === 'string' && serverMessage.trim()) {
      return serverMessage;
    }
    if (typeof err?.message === 'string' && err.message.trim()) {
      return err.message;
    }
    return fallback;
  }

  validatePhoneNumber(isCode = false, event?: Event): void {
    this.phoneErrorMessage.set(null);
    this.resetPhoneValidation();
    let phoneNumberStr;
    if (isCode) {
      phoneNumberStr = this.phoneForm.controls['phone'].value;
    } else {
      phoneNumberStr = (event?.target as HTMLInputElement).value;
    }
    let phoneNumber;
    if (phoneNumberStr[0] === '0') {
      phoneNumber = [...phoneNumberStr].slice(1).join('');
    } else {
      phoneNumber = phoneNumberStr;
    }
    const countryCode = this.selectedCountry.dial_code;
    const validCode = this.countries.filter(country => country.dial_code == countryCode);
    if (validCode.length && phoneNumber) {
      if (/^(0|[1-9][0-9]*)$/.test(phoneNumber) && PhoneNumberValidator(phoneNumber, validCode[0].code)) {
        this.invalidPhone = false;
      } else {
        this.invalidPhone = true;
      }
    }
    if ((validCode.length && !phoneNumber) || (countryCode && !validCode.length) || (phoneNumber && !validCode.length)) {
      this.invalidPhone = true;
    }

    if (!countryCode && !phoneNumber) {
      this.invalidPhone = false;
    }
  }


  startResendCooldown() {
    this.resendCooldown.set(true);
    this.resendCountdown.set(60);
    this.resendInterval = setInterval(() => {
      this.resendCountdown.update(value => value - 1);
      if (this.resendCountdown() <= 0) {
        this.resendCooldown.set(false);
        clearInterval(this.resendInterval);
      }
    }, 1000);
  }

  verifyPhoneSms() {
    // Validate the verification code before sending it to the server

    if (!this.verificationCode || this.verificationCode.trim().length === 0) {
      console.error('Verification code is required');
      return;
    }
    
    if (this.verificationCode.length < 6) {
      console.error('Verification code must be at least 6 digits');
      return;
    }
    
    if (!/^[0-9]+$/.test(this.verificationCode)) {
      console.error('Verification code must contain only numbers');
      return;
    }

    this.verifyingPhone.set(true);
    // No explicit token (logged-in flow) → the interceptor applies cookie-based auth.
    // The sign-up flow still passes a token via the `token` input.
    this._authService.verifyPhoneSms(this.verificationCode, this.token()).subscribe({
      next: (res: unknown) => {
        this.toastService.success('Your phone number has been successfully verified.', 'Success');
        this.closeModal.emit();
        this.verifyingPhone.set(false);
      },
      error: (error: unknown) => {
        // console.error('Error verifying phone number:', error);
        this.verifyingPhone.set(false);
      }
    });
  }
}